import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { resetDocumentTypes, setDocumentTypes, setDocumentTypesLoading } from '../../../store/processDocument/documentTypes'
import { useLazyGetExternalUserByIdQuery } from '../../../store/externalUser/externalUser'
import { useLazyGetAllDocumentTypesQuery } from '../../../store/ai/documentTypes'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetMeQuery } from '../../../store/user/user'
import {
  resetProcessDocumentUsers,
  setProcessDocumentContact,
  setProcessDocumentContactLoading,
  setProcessDocumentCurrentUser,
  setProcessDocumentCurrentUserIsLoading,
} from '../../../store/processDocument/documentUsers'
import { resetProcessDocumentInfo } from '../../../store/processDocument/documentInfo'
import { resetDocumentSummary } from '../../../store/processDocument/documentSummary'

export const useInitializeDocumentInfo = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetProcessDocumentInfo())
    }
  }, [dispatch])
}

export const useInitializeDocumentTypes = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetAllDocumentTypes] = useLazyGetAllDocumentTypesQuery()

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        dispatch(setDocumentTypesLoading(true))
        const { data: documentTypes } = await triggerGetAllDocumentTypes({ accountId })
        if (!documentTypes?.data) {
          return
        }
        dispatch(setDocumentTypes(documentTypes.data))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setDocumentTypesLoading(false))
      }
    }

    fetchDocumentTypes()

    return () => {
      dispatch(resetDocumentTypes())
    }
  }, [accountId, dispatch, triggerGetAllDocumentTypes])
}

export const useInitializeDocumentUsers = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const { userId = null } = useParams()

  const [triggerGetMe] = useLazyGetMeQuery()
  const [triggerGetContactById] = useLazyGetExternalUserByIdQuery()

  useEffect(() => {
    const fetchMe = async () => {
      dispatch(setProcessDocumentCurrentUserIsLoading(true))
      try {
        const { data: currentUser } = await triggerGetMe({ accountId }, true)
        if (!currentUser) {
          return
        }
        dispatch(setProcessDocumentCurrentUser(currentUser))
      } finally {
        dispatch(setProcessDocumentCurrentUserIsLoading(false))
      }
    }

    fetchMe()
  }, [accountId, dispatch, triggerGetMe])

  useEffect(() => {
    if (!userId) {
      return
    }

    const fetchContact = async () => {
      dispatch(setProcessDocumentContactLoading(true))
      try {
        const { data: contactResponse } = await triggerGetContactById({ accountId, userId })
        if (!contactResponse?.data) {
          return
        }
        dispatch(setProcessDocumentContact(contactResponse.data))
      } finally {
        dispatch(setProcessDocumentContactLoading(false))
      }
    }

    fetchContact()
  }, [dispatch, accountId, triggerGetContactById, userId])

  useEffect(() => {
    return () => {
      dispatch(resetProcessDocumentUsers())
    }
  }, [dispatch])
}

export const useInitializeDocumentSummary = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetDocumentSummary())
    }
  }, [dispatch])
}
